import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';

function Reports() {
  const [reports, setReports] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await axios.get(`${API_URL}/reports/service-history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      alert('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReports();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setTimeout(() => fetchReports(), 0);
  };

  const exportToExcel = () => {
    if (reports.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV content
    const headers = ['Date', 'Time', 'Client Name', 'Phone', 'Email', 'Service', 'Stylist', 'Amount', 'Payment Method'];
    const csvContent = [
      headers.join(','),
      ...reports.map(report => {
        // Handle multiple services for CSV export
        let serviceDisplay = report.service_name || '';
        if (report.services && report.services.length > 0) {
          if (report.services.length === 1) {
            serviceDisplay = report.services[0].name;
          } else {
            serviceDisplay = report.services.map(s => s.name).join('; ');
          }
        }

        return [
          report.appointment_date || '',
          report.appointment_time || '',
          `"${report.client_name || ''}"`,
          report.client_phone || '',
          report.client_email || '',
          `"${serviceDisplay}"`,
          `"${report.stylist_name || ''}"`,
          report.amount || '0',
          report.payment_method || ''
        ].join(',');
      })
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `service-history-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const totalRevenue = reports.reduce((sum, report) => sum + (parseFloat(report.amount) || 0), 0);

  return (
    <div className="reports-container">
      <div className="header">
        <h2>Service History Reports</h2>
        <div className="header-actions">
          <button onClick={exportToExcel} className="btn-primary">
            Export to Excel
          </button>
        </div>
      </div>

      <div className="reports-filters">
        <form onSubmit={handleSearch} className="filter-form">
          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary">Search</button>
            <button type="button" onClick={handleClearFilters} className="btn-secondary">Clear</button>
          </div>
        </form>
      </div>

      <div className="reports-summary">
        <div className="summary-card">
          <h3>Total Services</h3>
          <p className="summary-value">{reports.length}</p>
        </div>
        <div className="summary-card">
          <h3>Total Revenue</h3>
          <p className="summary-value">${totalRevenue.toFixed(2)}</p>
        </div>
      </div>

      {loading ? (
        <p>Loading reports...</p>
      ) : (
        <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Client</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Service</th>
                <th>Stylist</th>
                <th>Amount</th>
                <th>Payment Method</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>No records found</td>
                </tr>
              ) : (
                reports.map(report => {
                  // Handle multiple services display
                  let serviceDisplay = report.service_name || 'N/A';
                  if (report.services && report.services.length > 0) {
                    if (report.services.length === 1) {
                      serviceDisplay = report.services[0].name;
                    } else {
                      serviceDisplay = report.services.map(s => s.name).join(', ');
                    }
                  }

                  return (
                    <tr key={report.id}>
                      <td>{formatDate(report.appointment_date)}</td>
                      <td>{formatTime(report.appointment_time)}</td>
                      <td>{report.client_name}</td>
                      <td>{report.client_phone || 'N/A'}</td>
                      <td>{report.client_email || 'N/A'}</td>
                      <td>{serviceDisplay}</td>
                      <td>{report.stylist_name || 'N/A'}</td>
                      <td>${parseFloat(report.amount).toFixed(2)}</td>
                      <td className="payment-method">{report.payment_method}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
      )}
    </div>
  );
}



export default Reports;
